import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useState } from 'react';
import { ref, onValue, off } from 'firebase/database';
import { getDatabase } from 'firebase/database';
import { getApps, initializeApp } from 'firebase/app';
import BookingAPI from '../common/bookingApi';
import { FirebaseConfig } from '../config/FirebaseConfig';

/**
 * Hook untuk mendapatkan booking data dengan selector
 * Real-time updates tanpa manual fetch
 */
export const useBookingDataSelector = (bookingId) => {
  const [bookingData, setBookingData] = useState(null);
  const [driverInfo, setDriverInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const dispatch = useDispatch();

  // ✅ SOLUSI: Selector untuk mendapatkan booking data dari Redux store
  const selectorBookingData = useSelector((state) => {
    const activeBookings = state.bookinglistdata?.active || [];
    console.log('🔍 [useBookingDataSelector] Redux state debug:', {
      bookingId,
      hasBookingListData: !!state.bookinglistdata,
      activeBookingsCount: activeBookings.length,
      activeBookings: activeBookings.map(b => ({ id: b.id, status: b.status })),
      foundBooking: activeBookings.find(booking => booking.id === bookingId)
    });
    return activeBookings.find(booking => booking.id === bookingId);
  });

  // ✅ SOLUSI: Selector untuk settings
  const settings = useSelector((state) => state.settingsdata?.settings);

  // ✅ SOLUSI: Check if booking should be tracked
  const shouldTrack = selectorBookingData && 
    ['ACCEPTED', 'STARTED'].includes(selectorBookingData.status) &&
    settings?.share_trip;

  // ✅ SOLUSI: Update booking data dari selector
  useEffect(() => {
    if (selectorBookingData) {
      console.log('📍 Booking data updated from selector:', selectorBookingData);
      setBookingData(selectorBookingData);
      setLoading(false);
      setError(null);
    }
  }, [selectorBookingData]);

  // ✅ SOLUSI: Function untuk memastikan booking ada di Redux store
  const ensureBookingInReduxStore = async () => {
    if (!bookingId) return;
    
    try {
      console.log('🔄 [useBookingDataSelector] Ensuring booking is in Redux store:', bookingId);
      
      // Import api object dan dispatch fetchBookings
      const { api } = await import('common');
      dispatch(api.fetchBookings());
      
      console.log('✅ [useBookingDataSelector] fetchBookings dispatched');
    } catch (error) {
      console.error('❌ [useBookingDataSelector] Error ensuring booking in Redux store:', error);
    }
  };

  // ✅ SOLUSI: Real-time listener untuk update Redux store di incognito mode
  useEffect(() => {
    if (!bookingId) {
      setError('Booking ID is required');
      setLoading(false);
      return;
    }

    // Jika sudah ada data dari selector, tidak perlu real-time listener
    if (selectorBookingData) {
      console.log('📍 Using booking data from Redux selector, skipping real-time listener');
      return;
    }

    // Jika tidak ada data di selector, coba pastikan booking ada di Redux store
    console.log('⚠️ [useBookingDataSelector] No booking found in Redux store, attempting to ensure it exists');
    ensureBookingInReduxStore();

    console.log('🔥 Setting up real-time listener for Redux store update in incognito mode');
    
    // Initialize Firebase if not already initialized
    let app;
    if (!getApps().length) {
      app = initializeApp(FirebaseConfig);
      console.log('🔥 Firebase app initialized for Redux store update');
    } else {
      app = getApps()[0];
      console.log('🔥 Using existing Firebase app for Redux store update');
    }
    const database = getDatabase(app);

    // Listen to booking changes and update Redux store
    const bookingRef = ref(database, `bookings/${bookingId}`);
    
    const unsubscribe = onValue(bookingRef, (snapshot) => {
      if (snapshot.exists()) {
        const rawBookingData = snapshot.val();
        console.log('🔥 Real-time booking update for Redux store:', rawBookingData);
        
        // Format booking data seperti di bookinglistactions.js
        const formattedBookingData = {
          ...rawBookingData,
          id: bookingId,
          pickupAddress: rawBookingData.pickup?.add ?? '',
          dropAddress: rawBookingData.drop?.add ?? '',
          discount: rawBookingData.discount || 0,
          cashPaymentAmount: rawBookingData.cashPaymentAmount || 0,
          cardPaymentAmount: rawBookingData.cardPaymentAmount || 0
        };
        
        console.log('🔥 Formatted booking data:', formattedBookingData);
        console.log('🔥 Pickup address:', formattedBookingData.pickupAddress);
        console.log('🔥 Drop address:', formattedBookingData.dropAddress);
        
        // Update local state
        setBookingData(formattedBookingData);
        setLoading(false);
        setError(null);
        
        // Update Redux store untuk memicu re-render
        dispatch({
          type: 'UPDATE_BOOKING_FROM_REALTIME',
          payload: formattedBookingData
        });
      } else {
        console.log('🔥 No booking data found for Redux store update');
        setError('Booking not found');
        setLoading(false);
      }
    }, (error) => {
      console.error('🔥 Error in real-time Redux store update:', error);
      setError('Error in real-time update: ' + error.message);
      setLoading(false);
    });

    // Cleanup
    return () => {
      console.log('🔥 Cleaning up real-time Redux store listener');
      unsubscribe();
    };
  }, [bookingId, selectorBookingData, dispatch]);

  // ✅ SOLUSI: Fetch driver info jika booking data berubah
  useEffect(() => {
    if (bookingData && bookingData.driver && !driverInfo) {
      const fetchDriverInfo = async () => {
        try {
          console.log('📍 Fetching driver info for driver:', bookingData.driver);
          const driverResult = await BookingAPI.getPublicDriverInfo(bookingData.driver);
          if (driverResult.success) {
            console.log("📍 Driver info:", driverResult.data);
            setDriverInfo(driverResult.data);
          }
        } catch (err) {
          console.error('Error fetching driver info:', err);
        }
      };

      fetchDriverInfo();
    }
  }, [bookingData, driverInfo]);

  return {
    bookingData,
    driverInfo,
    loading,
    error,
    shouldTrack,
    isFromSelector: !!selectorBookingData,
    isRealTime: !!bookingData, // ✅ REAL-TIME jika ada booking data (dari selector atau API)
    ensureBookingInReduxStore // ✅ Function untuk memastikan booking ada di Redux store
  };
};
